import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClientSlideOut } from './vendor';
import ProjectPicker from './ProjectPicker';

/**
 * The row's "● Client · Task" label as a control: one button that opens the project
 * picker, and, beside it, a separate "Open client" button for the client's slide-out.
 *
 * The label is a button, so it holds plain text only. `ClientSlideOut`'s own trigger is a
 * button too, and a button inside a button is invalid HTML, which is why the slide-out is
 * driven from its own control (its controlled `open` mode).
 *
 * The popover is portalled to the body and positioned from the label's rectangle (below
 * it, or above when there is no room), because the week panel clips its overflow and an
 * in-flow popover would be cut off on the first and last rows. Scrolling or resizing
 * closes it rather than leaving it adrift. An outside mousedown closes it too, and Esc
 * closes it and returns focus to the label (Esc inside the picker is handled there).
 *
 * @param {object} props
 * @param {object} props.entry The entry: `client`, `client_id`, `task`, `task_id` and `service`
 *   (the free-text label, shown as a chip beside the button, never inside it).
 * @param {boolean} [props.disabled] The row's lock.
 * @param {() => Promise<{id: string, name: string}[]>} props.loadClients
 * @param {(clientId: string) => Promise<{id: string, title: string}[]>} props.loadTasks
 * @param {(choice: import('./ProjectPicker').ProjectChoice) => void} props.onChoose
 * @param {string} props.idPrefix
 * @param {object} [props.buttonProps] Extra attributes for the label button (the row's
 *   `aria-invalid` and `aria-describedby` after a failed project change).
 */
// The picker is w-72 and its list is capped at max-h-56; used to decide which way it opens.
const POPOVER_WIDTH = 288;
const POPOVER_HEIGHT = 320;

export default function EntryProject({ entry, disabled = false, loadClients, loadTasks, onChoose, idPrefix, buttonProps = {} }) {
  const [open, setOpen] = useState(false);
  const [slideOpen, setSlideOpen] = useState(false);
  const [placement, setPlacement] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const inside = rootRef.current?.contains(e.target) || popoverRef.current?.contains(e.target);
      if (!inside) setOpen(false);
    };
    // The picker's own list scrolls; only a scroll of something else leaves it adrift.
    const onScroll = (e) => {
      if (!popoverRef.current?.contains(e.target)) setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  /** Opens the popover under the label, or above it when the viewport has no room below. */
  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const room = window.innerHeight - rect.bottom;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8));
      setPlacement(room >= POPOVER_HEIGHT || rect.top < POPOVER_HEIGHT
        ? { left, top: rect.bottom + 4 }
        : { left, bottom: window.innerHeight - rect.top + 4 });
    }
    setOpen(true);
  };

  // A lock that lands while the popover is open (a save elsewhere on the page) closes it.
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const hasProject = !!(entry?.client || entry?.task);
  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative hidden lg:flex min-w-0 shrink items-center gap-1 max-w-[22rem] text-sm text-gray-500 dark:text-gray-400">
      <button
        ref={triggerRef}
        type="button"
        data-testid="row-project"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={hasProject ? 'Change project' : 'Add project'}
        title="Change project"
        disabled={disabled}
        onClick={toggle}
        {...buttonProps}
        className="flex min-w-0 items-center gap-1.5 rounded-md border border-transparent px-1.5 py-1 hover:border-gray-200 dark:hover:border-gray-700 focus:border-[#32B1FF] focus:outline-none focus:ring-1 focus:ring-[#32B1FF] disabled:opacity-60 disabled:hover:border-transparent"
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#32B1FF]" aria-hidden="true" data-testid="row-project-dot" />
        {hasProject ? (
          <>
            {entry?.client && <span className="min-w-0 truncate max-w-[10rem]" data-testid="row-client">{entry.client}</span>}
            {entry?.client && entry?.task && <span aria-hidden="true">·</span>}
            {entry?.task && (
              <span className="min-w-0 truncate max-w-[12rem]" data-testid="row-task" title={entry.task}>{entry.task}</span>
            )}
          </>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">Add project</span>
        )}
      </button>

      {entry?.service?.trim() && (
        <span
          data-testid="row-service"
          title={`Label: ${entry.service.trim()}`}
          className="max-w-[9rem] shrink-0 truncate rounded bg-sky-100 dark:bg-sky-900/30 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 dark:text-sky-300"
        >
          <span className="sr-only">Label: </span>
          {entry.service.trim()}
        </span>
      )}

      {entry?.client_id && entry?.client && (
        <>
          <button
            type="button"
            data-testid="row-client-open"
            aria-label={`Open ${entry.client}`}
            title={`Open ${entry.client}`}
            onClick={() => setSlideOpen(true)}
            className="shrink-0 rounded-md p-1 text-gray-400 hover:text-[#32B1FF] focus:outline-none focus:ring-1 focus:ring-[#32B1FF]"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
          {slideOpen && (
            <ClientSlideOut
              clientId={entry.client_id}
              clientName={entry.client}
              open
              onClose={() => setSlideOpen(false)}
            />
          )}
        </>
      )}

      {open && !disabled && placement && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-50"
          style={placement}
          data-testid="row-project-popover"
        >
          <ProjectPicker
            entry={entry}
            loadClients={loadClients}
            loadTasks={loadTasks}
            idPrefix={idPrefix}
            onClose={close}
            onChoose={(choice) => {
              setOpen(false);
              onChoose(choice);
            }}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
